import {
  capitalizeString,
  formatCurrency,
  formatDateTime,
  formatNumber,
  getTimezoneDate,
} from '../../../../utilities/helpers'

import logoUrl from '../../../../assets/creator-panel-logo.svg'

const UNASSIGNED_COLOR = '#e6e8ec'

const getLogoSrc = () => {
  if (!logoUrl) return ''
  if (/^https?:\/\//i.test(logoUrl)) return logoUrl
  if (logoUrl.startsWith('/')) return `${window.location.origin}${logoUrl}`
  return logoUrl
}

const collectHoldKillSeats = (holds, kills) => {
  const allHoldKillSeats = []
  const allHoldKillGASections = {}

  const addToGASections = (gaSeats) => {
    if (!gaSeats) return
    for (const [sectionId, seats] of Object.entries(gaSeats)) {
      if (!allHoldKillGASections[sectionId]) {
        allHoldKillGASections[sectionId] = []
      }
      allHoldKillGASections[sectionId].push(...seats)
    }
  }

  const processCategory = (category) => {
    allHoldKillSeats.push(...(category.seats || []))
    addToGASections(category.gaSeats)
    if (category.categories) {
      for (const subCategory of Object.values(category.categories)) {
        processCategory(subCategory)
      }
    }
  }

  for (const hold of Object.values(holds || {})) {
    processCategory(hold)
  }
  for (const kill of Object.values(kills || {})) {
    processCategory(kill)
  }

  return { allHoldKillSeats, allHoldKillGASections }
}

const sumRevenue = (level, allHoldKillSeats, allHoldKillGASections) => {
  const availableSeats = (level?.seats || []).filter(
    (seat) => !allHoldKillSeats.includes(seat)
  )
  const regularSeatsRevenue =
    parseFloat(level?.price || 0) * availableSeats.length

  const gaSeatsRevenue =
    Object.entries(level?.gaSeats || {}).reduce((acc, [sectionId, seats]) => {
      const heldCount = allHoldKillGASections[sectionId]?.length || 0
      const availableGASeats = Math.max(0, seats.length - heldCount)
      return acc + availableGASeats
    }, 0) * parseFloat(level?.price || 0)

  return regularSeatsRevenue + gaSeatsRevenue
}

const sumRevenues = (levels, holds, kills) => {
  const { allHoldKillSeats, allHoldKillGASections } = collectHoldKillSeats(
    holds,
    kills
  )
  return Object.values(levels || {}).reduce(
    (acc, level) =>
      acc + sumRevenue(level, allHoldKillSeats, allHoldKillGASections),
    0
  )
}

const sumCapacity = (priceLevels, holds, kills) => {
  const allPriceLevelsSeats = Object.values(priceLevels || {}).reduce(
    (acc, level) =>
      acc +
      (level.seats?.length || 0) +
      (level.gaSeats ? Object.values(level.gaSeats).flat().length : 0),
    0
  )

  const countCategorySeats = (category) => {
    let count = category.seats?.length || 0
    if (category.gaSeats) {
      count += Object.values(category.gaSeats).flat().length
    }
    if (category.categories) {
      count += Object.values(category.categories).reduce(
        (subAcc, subCategory) => subAcc + countCategorySeats(subCategory),
        0
      )
    }
    return count
  }

  const allHoldsSeats = Object.values(holds || {}).reduce(
    (acc, hold) => acc + countCategorySeats(hold),
    0
  )
  const allKillsSeats = Object.values(kills || {}).reduce(
    (acc, kill) => acc + countCategorySeats(kill),
    0
  )

  return allPriceLevelsSeats - allHoldsSeats - allKillsSeats
}

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const colorDot = (color) => {
  const fill = escapeHtml(color || UNASSIGNED_COLOR)
  return `<svg class="color-dot" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><circle cx="7" cy="7" r="6" fill="${fill}" stroke="rgba(0,0,0,0.12)" stroke-width="1"/></svg>`
}

const getSeatmapSvgMarkup = (background) => {
  const mapGroup = document.querySelector('.cef-seatmap-wrapper svg > g')
  if (!mapGroup) return ''

  const width = background?.width || 1000
  const height = background?.height || 1000
  const clone = mapGroup.cloneNode(true)
  clone.removeAttribute('transform')
  clone.querySelectorAll('[data-row-selector]').forEach((el) => el.remove())

  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${width} ${height}" width="100%" class="seatmap-svg">${clone.innerHTML}</svg>`
}

const getAvailableSeatCountForLevel = (
  level,
  allHoldKillSeatSet,
  allHoldKillGASections
) => {
  if (!level) return 0
  const regularSeatCount = (level.seats || []).filter(
    (seatId) => !allHoldKillSeatSet.has(seatId)
  ).length
  const gaSeatCount = Object.entries(level.gaSeats || {}).reduce(
    (total, [sectionId, seats]) => {
      const heldCount = allHoldKillGASections[sectionId]?.length || 0
      return total + Math.max(0, (seats?.length || 0) - heldCount)
    },
    0
  )
  return regularSeatCount + gaSeatCount
}

const buildPriceLevelRows = (
  priceLevels,
  unassignedSeats,
  getObjectTotalSeatCount,
  holds,
  kills
) => {
  const { allHoldKillSeats, allHoldKillGASections } = collectHoldKillSeats(
    holds,
    kills
  )
  const allHoldKillSeatSet = new Set(allHoldKillSeats)

  const rows = Object.values(priceLevels || {}).map((level) => {
    const seatCount = getAvailableSeatCountForLevel(
      level,
      allHoldKillSeatSet,
      allHoldKillGASections
    )
    const revenue = level.price
      ? sumRevenue(level, allHoldKillSeats, allHoldKillGASections)
      : 0
    return `
      <tr>
        <td class="level-cell">
          ${colorDot(level.color)}
          <span>${escapeHtml(level.name)}</span>
        </td>
        <td>${level.price ? escapeHtml(formatCurrency(level.price)) : '—'}</td>
        <td>${escapeHtml(formatNumber(seatCount))}</td>
        <td>${escapeHtml(formatCurrency(revenue))}</td>
      </tr>
    `
  })

  const unassigned =
    unassignedSeats?.unassigned || Object.values(unassignedSeats || {})[0]
  if (unassigned) {
    const unassignedCount = getObjectTotalSeatCount(unassigned)
    if (unassignedCount > 0) {
      rows.push(`
        <tr>
          <td class="level-cell">
            ${colorDot(UNASSIGNED_COLOR)}
            <span>Unassigned</span>
          </td>
          <td>—</td>
          <td>${escapeHtml(formatNumber(unassignedCount))}</td>
          <td>—</td>
        </tr>
      `)
    }
  }

  return rows.join('')
}

const buildPrintHtml = ({
  event,
  configuration,
  priceLevels,
  unassignedSeats,
  holds,
  kills,
  getObjectTotalSeatCount,
  background,
}) => {
  const totalRevenue = sumRevenues(priceLevels, holds, kills)
  const totalCapacity = sumCapacity(priceLevels, holds, kills)
  const avgPerSeat = totalCapacity > 0 ? totalRevenue / totalCapacity : 0
  const eventDate = event?.start
    ? formatDateTime(getTimezoneDate(event.start, event?.timezone), 'dateOnly')
    : ''
  const eventTime = event?.start
    ? formatDateTime(getTimezoneDate(event.start, event?.timezone), 'timeOnly')
    : ''
  const printedOn = formatDateTime(new Date())
  const mapName = configuration?.name
    ? capitalizeString(configuration.name)
    : 'Seat map'

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${escapeHtml(event?.name || 'Scaling Report')} — Print</title>
  <style>
    @page {
      margin: 0.6in;
    }
    *, *::before, *::after { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 32px 40px;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      font-size: 12px;
      color: #141416;
      line-height: 1.4;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    h1, h2, p { margin: 0; }
    .header { margin-bottom: 24px; border-bottom: 1px solid #e6e8ec; padding-bottom: 16px; }
    .header h1 { font-size: 20px; font-weight: 600; margin-bottom: 4px; }
    .header .meta { color: #777e91; font-size: 12px; }
    .header .meta span + span::before { content: " · "; }
    .sections { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px; }
    .card {
      border: 1px solid #e6e8ec;
      border-radius: 8px;
      padding: 16px 20px;
    }
    .card h2 {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #777e91;
      margin-bottom: 10px;
    }
    .financial-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
    }
    .financial-item .label { color: #777e91; font-size: 11px; margin-bottom: 2px; }
    .financial-item .value { font-size: 14px; font-weight: 600; }
    .financial-note { color: #777e91; font-size: 10px; margin-top: 8px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { text-align: left; padding: 8px 8px; border-bottom: 1px solid #eef0f3; }
    th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; color: #777e91; font-weight: 600; }
    td:last-child, th:last-child { text-align: right; }
    .level-cell {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .color-dot {
      flex-shrink: 0;
      display: inline-block;
      vertical-align: middle;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .seatmap-section { page-break-inside: avoid; margin-top: 8px; }
    .seatmap-section h2 { font-size: 14px; font-weight: 600; margin-bottom: 12px; }
    .seatmap-container {
      border: 1px solid #e6e8ec;
      border-radius: 8px;
      padding: 16px;
      background: #fff;
    }
    .seatmap-svg { max-height: 72vh; display: block; margin: 0 auto; }
    .print-footer {
      margin-top: 32px;
      padding-top: 20px;
      border-top: 1px solid #e6e8ec;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .print-footer img {
      height: 32px;
      width: auto;
      display: block;
    }
    @media print {
      body { padding: 0; }
      .sections { grid-template-columns: 1fr 1fr; }
      .seatmap-section { break-inside: avoid; page-break-inside: avoid; }
      .color-dot circle { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <header class="header">
    <h1>${escapeHtml(event?.name || 'Event')}</h1>
    <p class="meta">
      ${eventDate ? `<span>${escapeHtml(eventDate)}</span>` : ''}
      ${eventTime ? `<span>${escapeHtml(eventTime)}</span>` : ''}
      <span>${escapeHtml(mapName)}</span>
      <span>Printed ${escapeHtml(printedOn || '')}</span>
    </p>
  </header>

  <div class="sections">
    <section class="card">
      <h2>Financial information</h2>
      <div class="financial-grid">
        <div class="financial-item">
          <div class="label">Potential revenue</div>
          <div class="value">${escapeHtml(formatCurrency(totalRevenue))}</div>
        </div>
        <div class="financial-item">
          <div class="label">Sellable capacity</div>
          <div class="value">${escapeHtml(formatNumber(totalCapacity))}</div>
        </div>
        <div class="financial-item">
          <div class="label">Avg per seat</div>
          <div class="value">${escapeHtml(formatCurrency(avgPerSeat))}</div>
        </div>
      </div>
      <p class="financial-note">Kills and holds are excluded</p>
    </section>

    <section class="card">
      <h2>Price levels (${Object.keys(priceLevels || {}).length})</h2>
      <table>
        <thead>
          <tr>
            <th>Level</th>
            <th>Base price</th>
            <th>Seats</th>
            <th>Revenue</th>
          </tr>
        </thead>
        <tbody>
          ${buildPriceLevelRows(priceLevels, unassignedSeats, getObjectTotalSeatCount, holds, kills)}
        </tbody>
      </table>
    </section>
  </div>

  <section class="seatmap-section">
    <h2>Seat map</h2>
    <div class="seatmap-container">
      ${getSeatmapSvgMarkup(background)}
    </div>
  </section>

  <footer class="print-footer">
    <img src="${escapeHtml(getLogoSrc())}" alt="Blocktickets" />
  </footer>

  <script>
    (function () {
      var imgs = document.querySelectorAll('image, img');
      var loaded = 0;
      var total = imgs.length;
      var didPrint = false;

      function tryPrint() {
        if (didPrint) return;
        didPrint = true;
        window.print();
      }

      if (total === 0) {
        tryPrint();
        return;
      }

      function onDone() {
        loaded++;
        if (loaded >= total) tryPrint();
      }

      imgs.forEach(function (img) {
        if (img.complete) {
          onDone();
        } else {
          img.addEventListener('load', onDone);
          img.addEventListener('error', onDone);
        }
      });

      setTimeout(tryPrint, 2000);
    })();
  </script>
</body>
</html>`
}

export const printScalingSeatmap = ({
  event,
  configuration,
  priceLevels,
  unassignedSeats,
  holds,
  kills,
  getObjectTotalSeatCount,
  background,
}) => {
  const html = buildPrintHtml({
    event,
    configuration,
    priceLevels,
    unassignedSeats,
    holds,
    kills,
    getObjectTotalSeatCount,
    background,
  })

  const blob = new Blob([html], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  const iframe = document.createElement('iframe')
  iframe.style.position = 'absolute'
  iframe.style.left = '-9999px'
  iframe.style.width = '1px'
  iframe.style.height = '1px'
  iframe.src = url

  document.body.appendChild(iframe)

  const cleanup = () => {
    if (document.body.contains(iframe)) {
      document.body.removeChild(iframe)
    }
    URL.revokeObjectURL(url)
  }

  iframe.onload = () => {
    window.addEventListener('focus', cleanup, { once: true })
  }
}
